/**
 * What a customer SEES as the price, decided from what checkout will CHARGE.
 *
 * Regulamentul BNR 4/2005 art. 3: a Romanian buyer is charged in lei (charge-currency.ts).
 * The app must show the same number Stripe Checkout will, else a Romanian customer reads
 * "39,99 EUR" in the CRM and "2xx lei" on the payment page.
 *
 * PURE and client-safe on purpose (no Stripe, no Next, no Supabase): the decision and the
 * formatter are what the tests pin; the server funnel (display-price.ts) only feeds them.
 *
 * FALLBACK (decided): a Romanian buyer whose Price has no lei amount YET (the
 * ron-price-refresh cron has not written it) is shown EUR. Checkout itself refuses such a
 * buyer (assertPricesChargeableIn), so this is a display-only softening, never an EUR sale.
 */
import type { ChargeCurrency } from './charge-currency';
import { RON_FX_MARKUP, formatMarkupPercent, formatRoDecimal } from './ron-pricing';
import { MODULE_CATALOG, SUBSCRIPTION_PLANS, type ModuleId, type PlanId } from './stripe/plans';

export type DisplayCurrency = ChargeCurrency;

export interface DisplayPrice {
  /** Currency of `amountMinor`: what the buyer will be charged in. */
  currency: DisplayCurrency;
  /** Charged amount in minor units (bani for ron, cents for eur). */
  amountMinor: number;
  /** The EUR list price in cents (always present; the secondary figure for lei buyers). */
  eurMinor: number;
}

export interface FormattedDisplayPrice {
  /** "207,67 lei" or "€39.99" */
  amount: string;
  /** "/ lună" or "/month" */
  unit: string;
  /** "39,99 EUR" for a lei price, null for an EUR price. */
  secondary: string | null;
  /** amount + unit: "207,67 lei / lună" or "€39.99/month" */
  full: string;
  /** full + secondary in brackets: "207,67 lei / lună (39,99 EUR)" */
  fullWithSecondary: string;
}

/** One catalogue Price: what Stripe holds for it (null = unknown / not written yet). */
export interface CatalogPriceAmounts {
  eurMinor: number | null;
  ronMinor: number | null;
}

/**
 * The display decision. lei only when the buyer is charged in lei AND the Price carries a
 * lei amount; everything else is the EUR list price.
 */
export function decideDisplayPrice(params: {
  chargeCurrency: DisplayCurrency;
  /** EUR list price, cents (Stripe's own value when known, else the catalogue's). */
  eurMinor: number;
  /** currency_options.ron.unit_amount in bani, or null when the Price has none. */
  ronMinor: number | null | undefined;
}): DisplayPrice {
  const { chargeCurrency, eurMinor, ronMinor } = params;
  if (chargeCurrency === 'ron' && typeof ronMinor === 'number' && ronMinor > 0) {
    return { currency: 'ron', amountMinor: ronMinor, eurMinor };
  }
  return { currency: 'eur', amountMinor: eurMinor, eurMinor };
}

/** The unchanged EUR price (what every non-Romanian buyer, and every fallback, sees). */
export function eurDisplayPrice(eurAmount: number): DisplayPrice {
  const eurMinor = Math.round(eurAmount * 100);
  return { currency: 'eur', amountMinor: eurMinor, eurMinor };
}

/**
 * Format for the UI. A lei price is Romanian-native ("207,67 lei / lună"; the buyer is a
 * Romanian company); an EUR price keeps the app's existing English "€39.99/month".
 */
export function formatDisplayPrice(price: DisplayPrice): FormattedDisplayPrice {
  if (price.currency === 'ron') {
    const amount = `${formatRoDecimal(price.amountMinor / 100, 2)} lei`;
    const unit = '/ lună';
    const secondary = `${formatRoDecimal(price.eurMinor / 100, 2)} EUR`;
    const full = `${amount} ${unit}`;
    return { amount, unit, secondary, full, fullWithSecondary: `${full} (${secondary})` };
  }
  // @format-helper-currency-glyph-ok reason=CMG-platform-list-price-is-EUR
  const amount = `€${price.amountMinor / 100}`;
  const unit = '/month';
  const full = `${amount}${unit}`;
  return { amount, unit, secondary: null, full, fullWithSecondary: full };
}

/**
 * The price AS BILLED by a live subscription: its own Stripe currency decides, never the
 * org's checkout decision. A Romanian org whose subscription still bills EUR (created
 * before lei billing, until migrated) is shown EUR; a RON subscription is shown the lei
 * amount the Price carries. Unknown currency, or RON without a lei amount -> null (the
 * caller shows the EUR amount it already has).
 */
export function decideLiveDisplayPrice(params: {
  liveCurrency: string | null | undefined;
  eurMinor: number;
  ronMinor: number | null | undefined;
}): DisplayPrice | null {
  const { liveCurrency, eurMinor, ronMinor } = params;
  if (liveCurrency === 'eur') return { currency: 'eur', amountMinor: eurMinor, eurMinor };
  if (liveCurrency === 'ron' && typeof ronMinor === 'number' && ronMinor > 0) {
    return { currency: 'ron', amountMinor: ronMinor, eurMinor };
  }
  return null;
}

/** Wire shape of the funnel: one entry per plan / module that has a Stripe price. */
export interface OrgDisplayPrices {
  currency: DisplayCurrency;
  /** True when a source (tax id, country, Stripe customer) named the buyer's country. */
  countryKnown: boolean;
  /** What a NEW checkout charges (store, upgrade prompts, reactivation). */
  plans: Partial<Record<string, DisplayPrice>>;
  modules: Partial<Record<string, DisplayPrice>>;
  /**
   * What the org's LIVE subscriptions bill (CurrentPlanCard, ModuleSubscriptionCard): only
   * the org's subscribed plan / modules, keyed by id; a missing entry = currency unknown.
   */
  live: {
    plans: Partial<Record<string, DisplayPrice>>;
    modules: Partial<Record<string, DisplayPrice>>;
  };
}

/** Plan price for display: the funnel's value, else the EUR catalogue price. */
export function planDisplayPrice(
  prices: OrgDisplayPrices | null | undefined,
  planId: PlanId
): DisplayPrice {
  return prices?.plans[planId] ?? eurDisplayPrice(SUBSCRIPTION_PLANS[planId].price);
}

/** Module price for display: the funnel's value, else the EUR catalogue price. */
export function moduleDisplayPrice(
  prices: OrgDisplayPrices | null | undefined,
  moduleId: ModuleId
): DisplayPrice {
  return prices?.modules[moduleId] ?? eurDisplayPrice(MODULE_CATALOG[moduleId].price);
}

/** The org's live plan subscription as billed; unknown -> the subscription's EUR amount. */
export function livePlanDisplayPrice(
  prices: OrgDisplayPrices | null | undefined,
  planId: string,
  eurAmount: number
): DisplayPrice {
  return prices?.live?.plans[planId] ?? eurDisplayPrice(eurAmount);
}

/** The org's live module subscription as billed; unknown -> the EUR catalogue price. */
export function liveModuleDisplayPrice(
  prices: OrgDisplayPrices | null | undefined,
  moduleId: ModuleId
): DisplayPrice {
  return prices?.live?.modules[moduleId] ?? eurDisplayPrice(MODULE_CATALOG[moduleId].price);
}

// ---------------------------------------------------------------------------
// Conversation overage copy (adversarial N1, 2026-10-06).
//
// The overage is a EUR LIST price (plans.ts quota.overagePrice) charged in the currency of
// the module subscription it rides on: EUR, or lei converted at charge time
// (conversation-overage-billing.ts resolveOverageCharge). So a Romanian org reads it in
// Romanian, quoted in EUR as the list price, with the lei-billing note when it is billed
// in lei; never a euro glyph. Everyone else keeps the app's English "€5/50".
// Plain data on purpose: a server component hands it to a client card as a prop.
// ---------------------------------------------------------------------------

/** Who reads the overage copy (language) and what currency the overage is billed in. */
export interface OverageBilling {
  /** The org's display funnel currency: 'ron' = a Romanian org (Romanian copy). */
  displayCurrency: DisplayCurrency;
  /** What the overage invoice item is charged in. */
  billedCurrency: DisplayCurrency;
}

/**
 * The overage billing facts for one module, from the funnel: billed in the live module
 * subscription's own currency when known (a Romanian org still on an EUR subscription is
 * billed EUR), else in what a new checkout charges. No funnel -> EUR / English.
 */
export function moduleOverageBilling(
  prices: OrgDisplayPrices | null | undefined,
  moduleId: ModuleId
): OverageBilling {
  const displayCurrency = prices?.currency ?? 'eur';
  return {
    displayCurrency,
    billedCurrency: prices?.live?.modules[moduleId]?.currency ?? displayCurrency,
  };
}

export interface OverageCopy {
  /** Romanian copy (a Romanian org). */
  ro: boolean;
  /** An EUR amount: "€15" (English) or "15 EUR" (Romanian, no glyph). */
  money: (eur: number) => string;
  /** RO only: "5 EUR / 50 conversații suplimentare"; null in English. */
  short: string | null;
  /** RO only: short + the lei-billing note when billed in lei; null in English. */
  full: string | null;
  /** RO + billed in lei: "facturat în lei la cursul BNR + 2%"; else null. */
  billingNote: string | null;
}

function formatEurRo(eur: number): string {
  return Number.isInteger(eur) ? String(eur) : formatRoDecimal(eur, 2);
}

/**
 * The lei-billing hint shown before the buyer is identified (signup, plan choice): ONE
 * copy for every surface, built from RON_FX_MARKUP so it can never drift from what the
 * charge actually adds on top of the BNR rate.
 */
export const RON_BILLING_HINT = {
  en: `Companies in Romania are billed in lei at the BNR rate + ${formatMarkupPercent(RON_FX_MARKUP)}.`,
  ro: `Firmele din România plătesc în lei, la cursul BNR + ${formatMarkupPercent(RON_FX_MARKUP)}.`,
} as const;

/** The one formatter for every customer-visible overage money string. */
export function overageCopy(
  billing: OverageBilling | null | undefined,
  quota: { overagePrice: number; overageBlockSize: number }
): OverageCopy {
  if (billing?.displayCurrency !== 'ron') {
    // @format-helper-currency-glyph-ok reason=CMG-platform-list-price-is-EUR
    return { ro: false, money: (eur) => `€${eur}`, short: null, full: null, billingNote: null };
  }
  const money = (eur: number) => `${formatEurRo(eur)} EUR`;
  const short = `${money(quota.overagePrice)} / ${quota.overageBlockSize} conversații suplimentare`;
  const billingNote =
    billing.billedCurrency === 'ron'
      ? `facturat în lei la cursul BNR + ${formatMarkupPercent(RON_FX_MARKUP)}`
      : null;
  return {
    ro: true,
    money,
    short,
    full: billingNote ? `${short}, ${billingNote}` : short,
    billingNote,
  };
}
